import { useId, type ReactNode } from "react";
import { cn } from "../../../components/ui";

// ===========================================================================
// Teléfono con lada (código de país). Se guarda como "+52 5512345678".
// ===========================================================================

export const COUNTRY_CODES = [
  { code: "+52", label: "🇲🇽 +52" },
  { code: "+1", label: "🇺🇸 +1" },
  { code: "+502", label: "🇬🇹 +502" },
  { code: "+503", label: "🇸🇻 +503" },
  { code: "+504", label: "🇭🇳 +504" },
  { code: "+505", label: "🇳🇮 +505" },
  { code: "+506", label: "🇨🇷 +506" },
  { code: "+507", label: "🇵🇦 +507" },
  { code: "+57", label: "🇨🇴 +57" },
  { code: "+51", label: "🇵🇪 +51" },
  { code: "+56", label: "🇨🇱 +56" },
  { code: "+54", label: "🇦🇷 +54" },
  { code: "+34", label: "🇪🇸 +34" },
] as const;

const DEFAULT_CODE = "+52";
// Las ladas más largas primero, para que "+502…" no se lea como "+50" o "+5".
const BY_LENGTH = [...COUNTRY_CODES].sort((a, b) => b.code.length - a.code.length);

/** Separa "+52 55 1234 5678" en lada y número (sólo dígitos). Sin lada conocida, México. */
export function splitPhone(value: string | null | undefined): { code: string; number: string } {
  const raw = String(value ?? "").trim();
  const compact = raw.replace(/[\s\-.()]/g, "");
  if (compact.startsWith("+")) {
    const hit = BY_LENGTH.find((c) => compact.startsWith(c.code));
    if (hit) return { code: hit.code, number: compact.slice(hit.code.length).replace(/\D/g, "") };
  }
  return { code: DEFAULT_CODE, number: compact.replace(/\D/g, "") };
}

export const joinPhone = (code: string, number: string) => `${code} ${number.replace(/\D/g, "")}`;

/** México: 10 dígitos; otros países: entre 7 y 12. */
export function isValidPhone(code: string, number: string): boolean {
  const digits = number.replace(/\D/g, "");
  return code === "+52" ? digits.length === 10 : digits.length >= 7 && digits.length <= 12;
}

export function PhoneField({
  label,
  code,
  number,
  onChange,
  error,
  required,
  hint,
}: {
  label: ReactNode;
  code: string;
  number: string;
  onChange: (next: { code: string; number: string }) => void;
  error?: ReactNode;
  required?: boolean;
  hint?: ReactNode;
}) {
  const id = useId();
  const control = (invalid: boolean) =>
    cn(
      "h-10 rounded-lg border bg-app px-3 text-sm text-content focus:outline-none focus:ring-2 focus:ring-primary-500",
      invalid ? "border-error" : "border-line",
    );
  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={`${id}-number`} className="text-sm font-medium text-content">
        {label}
        {required ? <span className="ml-0.5 text-error">*</span> : null}
      </label>
      <div className="flex gap-2">
        <select
          aria-label="Lada"
          value={code}
          onChange={(e) => onChange({ code: e.target.value, number })}
          className={cn(control(false), "w-28 shrink-0")}
        >
          {COUNTRY_CODES.map((c) => (
            <option key={c.code} value={c.code}>{c.label}</option>
          ))}
        </select>
        <input
          id={`${id}-number`}
          type="tel"
          inputMode="numeric"
          autoComplete="tel-national"
          required={required}
          aria-invalid={error ? true : undefined}
          value={number}
          onChange={(e) => onChange({ code, number: e.target.value.replace(/[^\d\s]/g, "") })}
          placeholder={code === "+52" ? "55 1234 5678" : "Número"}
          className={cn(control(!!error), "min-w-0 flex-1")}
        />
      </div>
      {error ? <p className="text-xs text-error">{error}</p> : hint ? <p className="text-xs text-content-muted">{hint}</p> : null}
    </div>
  );
}
